import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { BisacCode, BisacSearchHit } from "reedy-data/api";
import { TableCell, TableRow } from "components/table";
import { Text } from "components/text";
import { ThemaCodesTableRowHighlightedText } from "components/ThemaCodes/ThemaCodesTableRowHighlightedText";
import { formatBisacHeading } from "shared/bisac/formatBisacHeading";
import { cn } from "utils";
import { toBisacCodePrefix } from "./toBisacCodePrefix";

// No horizontal padding on the mark, so the notation still reads as one code.
const HighlightedNotation = ({
  code,
  query,
}: {
  code: string;
  query: string;
}) => {
  const prefix = toBisacCodePrefix(query);
  if (!prefix || !code.startsWith(prefix)) return code;

  return (
    <>
      <mark className="bg-yellow-200 rounded-sm">{prefix}</mark>
      {code.slice(prefix.length)}
    </>
  );
};

export const BisacCodesTableCodeRow = ({
  code,
  highlightPrefix,
  indented = false,
  renderAction,
}: {
  code: BisacSearchHit;
  highlightPrefix?: string;
  indented?: boolean;
  renderAction?: (code: BisacCode) => ReactNode;
}) => {
  const { t } = useTranslation("components");
  const seeAlso = code.seeAlso.join("; ");

  return (
    <TableRow className="text-sm group" data-testid={`bisac-row-${code.code}`}>
      <TableCell className={cn("py-2", indented ? "pl-8" : "pl-4")}>
        <HighlightedNotation code={code.code} query={highlightPrefix ?? ""} />
      </TableCell>
      <TableCell className="py-2 whitespace-normal">
        <span className="break-words">
          <ThemaCodesTableRowHighlightedText
            text={code.highlightedHeading}
            fallback={formatBisacHeading(code)}
            markClassName="px-0"
          />
          {seeAlso && (
            <Text as="span" variant="muted">
              {" "}
              {t("components:bisacCodesTable.seeAlso", { seeAlso })}
            </Text>
          )}
        </span>
      </TableCell>
      <TableCell className="py-2 px-4 text-right">
        {renderAction?.(code)}
      </TableCell>
    </TableRow>
  );
};
